import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip, Tabs, Tab, Switch, Checkbox, CircularProgress } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import CodeIcon from "@mui/icons-material/Code";
import { setModal, cleanIsUpdate } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";
import toast from "react-hot-toast";
import apiClient from "../../services/apiClient";

const Tools = ({ formData = {}, setFormData = null, editMode = false }) => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);
  const [activeTab, setActiveTab] = useState(0);

  // Dynamic tools from API
  const [tools, setTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);


  // Selected tools (array of tool IDs)
  const [selectedTools, setSelectedTools] = useState([]);

  // Static categories
  const STATIC_CATEGORIES = [
    { id: "scheduling", name: "Scheduling", color: { bg: "#E3F2FD", color: "#1565C0" } },
    { id: "communication", name: "Communication", color: { bg: "#E8F5E9", color: "#2E7D32" } },
    { id: "data", name: "Data", color: { bg: "#FFF3E0", color: "#E65100" } },
    { id: "automation", name: "Automation", color: { bg: "#F3E5F5", color: "#7B1FA2" } },
    { id: "analytics", name: "Analytics", color: { bg: "#FCE4EC", color: "#C2185B" } },
    { id: "other", name: "Other", color: { bg: "#F5F5F5", color: "#666" } },
  ];

  const [customTools, setCustomTools] = useState([
    {
      id: 1,
      name: "Order Status Checker",
      type: "API",
      endpoint: "https://api.company.com/orders",
      method: "GET",
      status: "Active",
      lastUsed: "2024-01-15 10:30 AM",
      executions: 156,
    },
    {
      id: 2,
      name: "Inventory Update",
      type: "Webhook",
      endpoint: "https://webhook.company.com/inventory",
      method: "POST",
      status: "Active",
      lastUsed: "2024-01-15 09:15 AM",
      executions: 89,
    },
    {
      id: 3,
      name: "Customer Verification",
      type: "Database",
      endpoint: "SELECT * FROM customers WHERE...",
      method: "SQL",
      status: "Inactive",
      lastUsed: "2024-01-10 03:20 PM",
      executions: 45,
    },
  ]);

  const [loading, setLoading] = useState(false);

  // Initialize component - fetch tools
  useEffect(() => {
    fetchTools();
  }, []);

  // Pre-fill selected tools in edit mode
  useEffect(() => {
    if (editMode && formData?.tools && Array.isArray(formData.tools)) {
      setSelectedTools(formData.tools);
    }
  }, [editMode, formData?.tools]);

  // Update modal listener - fetch tools immediately after successful edit/delete/add
  useEffect(() => {
    if (modal?.isUpdated?.isUpdatedIn === "TOOLS") {
      fetchTools();
      // Clean up the flag to allow future updates
      dispatch(cleanIsUpdate());
    }
  }, [modal?.isUpdated]);

  // Fetch tools from API
  const fetchTools = async () => {
    setLoadingTools(true);
    try {
      const res = await apiClient.get("/tools");
      if (res?.status && Array.isArray(res.data)) {
        setTools(res.data);
      } else {
        toast.error("Failed to load tools");
      }
    } catch (err) {
      console.error("Error fetching tools:", err);
      toast.error("Error fetching tools");
    } finally {
      setLoadingTools(false);
    }
  };

  // Handle tool selection
  const handleToolSelection = (toolId, isSelected) => {
    let updatedSelectedTools;
    if (isSelected) {
      updatedSelectedTools = [...selectedTools, toolId];
    } else {
      updatedSelectedTools = selectedTools.filter((id) => id !== toolId);
    }
    setSelectedTools(updatedSelectedTools);

    // Update formData if setFormData is provided
    if (setFormData) {
      setFormData((prev) => ({
        ...prev,
        tools: updatedSelectedTools,
      }));
    }
  };

  // Handle tool status toggle
  const handleStatusToggle = async (toolId, currentStatus) => {
    if (!toolId) {
      toast.error("Invalid tool ID");
      return;
    }

    try {
      // Update locally first for immediate UI feedback (optimistic update)
      setTools((prevTools) =>
        prevTools.map((tool) =>
          tool._id === toolId ? { ...tool, status: !currentStatus } : tool
        )
      );

      // Make API call to update status on backend
      const response = await apiClient.put(`/tools/${toolId}`, {
        status: !currentStatus
      });

      // Verify the response
      if (response?.status && response?.data) {
        toast.success(response.message || "Tool status updated successfully");

        // Update with actual server response to ensure consistency
        setTools((prevTools) =>
          prevTools.map((tool) =>
            tool._id === toolId ? { ...tool, ...response.data } : tool
          )
        );
      } else {
        throw new Error(response?.message || "Invalid response from server");
      }
    } catch (err) {
      console.error("Error updating tool status:", err);

      // Show specific error message if available
      const errorMessage = err.response?.data?.message || err.message || "Failed to update tool status";
      toast.error(errorMessage);

      // Revert the optimistic update on error
      setTools((prevTools) =>
        prevTools.map((tool) =>
          tool._id === toolId ? { ...tool, status: currentStatus } : tool
        )
      );
    }
  };

  // Get category info
  const getCategoryInfo = (categoryName) => {
    const category = STATIC_CATEGORIES.find(
      (cat) => cat.name.toLowerCase() === (categoryName || "").toLowerCase()
    );
    return category ? category.color : STATIC_CATEGORIES[STATIC_CATEGORIES.length - 1].color;
  };

  const handleCreateTool = () => {
    dispatch(setModal({ type: "ADD_TOOL" }));
  };

  const handleEditTool = (tool) => {
    if (!tool?._id) {
      toast.error("Invalid tool data");
      return;
    }
    // Pass the complete tool object with all fields
    dispatch(setModal({
      type: "EDIT_TOOL",
      data: {
        _id: tool._id,
        name: tool.name,
        function: tool.function,
        category: tool.category,
        status: tool.status, // Pass exact boolean value
        created_at: tool.created_at,
        updated_at: tool.updated_at
      }
    }));
  };

  const handleDeleteTool = (tool) => {
    if (window.confirm(`Delete tool "${tool.name}"?`)) {
      setCustomTools(customTools.filter((t) => t.id !== tool.id));
    }
  };

  const handleTestTool = (tool) => {
    dispatch(setModal({ type: "TEST_TOOL", data: tool }));
  };

  const getCategoryColor = (category) => {
    return getCategoryInfo(category);
  };

  const getTypeColor = (type) => {
    const colors = {
      API: { bg: "#E3F2FD", color: "#1565C0" },
      Webhook: { bg: "#F3E5F5", color: "#7B1FA2" },
      Database: { bg: "#FFF3E0", color: "#E65100" },
    };
    return colors[type] || { bg: "#F5F5F5", color: "#666" };
  };

  const prebuiltColumns = useMemo(
    () => [
      {
        accessorKey: "select",
        header: "Select",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Checkbox
            checked={selectedTools.includes(row.original._id)}
            onChange={(e) => handleToolSelection(row.original._id, e.target.checked)}
            color="primary"
          />
        ),
      },
      {
        accessorKey: "name",
        header: "Tool Name",
        size: 200,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {row.original.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.function}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "category",
        header: "Category",
        size: 130,
        Cell: ({ row }) => {
          const categoryName = row.original.category || "Other";
          const colors = getCategoryColor(categoryName);
          return (
            <Chip
              label={categoryName}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={row.original.status}
              onChange={() => handleStatusToggle(row.original._id, row.original.status)}
              size="small"
              color="success"
            />
            <Typography variant="body2" color="text.secondary">
              {row.original.status ? "Active" : "Inactive"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        size: 150,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          if (!date) return "-";
          return (
            <Typography variant="body2" color="text.secondary">
              {new Date(date).toLocaleDateString()}
            </Typography>
          );
        },
      },
    ],
    [selectedTools]
  );

  const customColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Tool Name",
        size: 180,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <CodeIcon fontSize="small" />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {row.original.name}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 110,
        Cell: ({ cell }) => {
          const colors = getTypeColor(cell.getValue());
          return (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "endpoint",
        header: "Endpoint",
        size: 250,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: 11,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "method",
        header: "Method",
        size: 90,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            sx={{
              fontSize: 10,
              height: 20,
              backgroundColor: "#F3F4F6",
              color: "#666",
              fontWeight: 600,
            }}
          />
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={cell.getValue() === "Active" ? "success" : "default"}
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: "executions",
        header: "Executions",
        size: 110,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            {cell.getValue()}
          </Typography>
        ),
      },
    ],
    []
  );

  // const totalTools = tools.length + customTools.length;
  // const enabledTools = tools.filter((t) => t.status).length + customTools.filter((t) => t.status === "Active").length;
  // const selectedToolsCount = selectedTools.length;

  // 🔥 Dynamic counters based only on API tools
  const totalTools = tools.length;
  const activeTools = tools.filter((t) => t.status === true).length;
  const selectedToolsCount = selectedTools.length;
  const availableTools = tools.length; // same as total tools


  return (


    <>
    
      <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
        <div>
          <h3>Tools</h3>
          <p>Manage pre-built tools and create custom API integrations</p>
        </div>
        <CustomButton
          icon={<AddIcon />}
          iconPosition="left"
          onClick={handleCreateTool}
          text="Create Custom Tool"
        />

      </div>
    
    <Box>
      {/* Header Section - White Background */}
      {/* <Box sx={{
        backgroundColor: "#FFFFFF",
        borderBottom: "1px solid #E5E7EB",
        px: 4,
        py: 3
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              Tools
            </Typography>
            <Typography variant="body2" color="#6B7280">
              Manage pre-built tools and create custom API integrations
            </Typography>
          </Box>
        </Box>
      </Box> */}

      {/* Stats Cards Section - Separate */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              {totalTools}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Tools
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#10B981" sx={{ mb: 0.5 }}>
              {activeTools}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Active Tools
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#3B82F6" sx={{ mb: 0.5 }}>
              {selectedToolsCount}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Selected Tools
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#F59E0B" sx={{ mb: 0.5 }}>
              {availableTools}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Available Tools
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ px: 4, pb: 3 }}>
        <Box sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          overflow: "hidden"
        }}>
          <Box sx={{ borderBottom: 1, borderColor: "#E5E7EB" }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  '&.Mui-selected': {
                    color: '#111827',
                  }
                }
              }}
            >
              <Tab label={`Available Tools (${tools.pagination?.total_count || 0})`} />
              <Tab label="Custom Tools" />
            </Tabs>
          </Box>

          <Box sx={{ p: 2 }}>
            {activeTab === 0 ? (
              loadingTools ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <MaterialReactTable
                  columns={prebuiltColumns}
                  data={tools}
                  state={{ isLoading: loadingTools }}
                  enableRowActions
                  positionActionsColumn="last"
                  renderRowActions={({ row }) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Configure">
                        <IconButton
                          size="small"
                          onClick={() => handleEditTool(row.original)}
                          sx={{ color: "primary.main" }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  enableColumnFilters
                  enableGlobalFilter
                  enableSorting
                  enablePagination={false}
                  muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: 0,
                      border: "none",
                      boxShadow: "none"
                    },
                  }}
                  muiTableBodyProps={{
                    sx: {
                      '& tr:hover': {
                        backgroundColor: '#F9FAFB',
                      },
                    },
                  }}
                />
              )
            ) : (
              <MaterialReactTable
                columns={customColumns}
                data={customTools}
                state={{ isLoading: loading }}
                enableRowActions
                positionActionsColumn="last"
                renderRowActions={({ row }) => (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Test Tool">
                      <IconButton
                        size="small"
                        onClick={() => handleTestTool(row.original)}
                        sx={{ color: "success.main" }}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTool(row.original)}
                        sx={{ color: "primary.main" }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTool(row.original)}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                enableColumnFilters
                enableGlobalFilter
                enableSorting
                enablePagination
                initialState={{
                  pagination: { pageSize: 10, pageIndex: 0 },
                }}
                muiTablePaperProps={{
                  elevation: 0,
                  sx: {
                    borderRadius: 0,
                    border: "none",
                    boxShadow: "none"
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
    
    
    
    </>


  );
};

export default Tools;
